import { useState, useRef, useCallback } from "react";

import { Layout } from "../common";

const MAX_SIZE = 5 * 1024 * 1024; // 5MB
const API_BASE = "https://img-compare-app.azurewebsites.net";
const API_KEY = "imgcmp-s3cur3-k3y-ch4ng3-v1";
const POLL_INTERVAL = 5000; // 5 seconds

interface ImageData {
  dataUrl: string;
  file: File;
  mimeType: string;
}

type ComparisonMode = "compare" | "localize";

interface BBox {
  label: string;
  bbox: [number, number, number, number];
}

interface CompareResult {
  result: string;
}

interface LocalizeResult {
  bboxes: BBox[];
  raw_response: string;
  annotated_image: string;
}

interface TaskResponse {
  task_id: string;
  status: "pending" | "completed" | "failed";
  type: string;
  created_at: string;
  result?: CompareResult | LocalizeResult;
  error?: string;
}

interface ImageUploadPanelProps {
  label: string;
  image: ImageData | null;
  onUpload: (data: ImageData) => void;
  onRemove: () => void;
}

function ImageUploadPanel({
  label,
  image,
  onUpload,
  onRemove
}: ImageUploadPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const processFile = (file: File) => {
    if (!file.type.startsWith("image/"))
      return alert("Please upload an image file.");
    if (file.size > MAX_SIZE) return alert("Image must be under 5MB.");
    const reader = new FileReader();
    reader.onload = () =>
      onUpload({ dataUrl: reader.result as string, file, mimeType: file.type });
    reader.readAsDataURL(file);
  };

  return (
    <div className="upload-panel">
      <div className="panel-header">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
        <span>{label}</span>
      </div>

      <div
        className={`drop-zone ${image ? "has-image" : ""}`}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => !image && inputRef.current?.click()}
      >
        {image ? (
          <>
            <img src={image.dataUrl} alt="Uploaded" className="preview-img" />
            <button
              className="remove-btn"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
            >
              Remove
            </button>
          </>
        ) : (
          <div className="upload-prompt">
            <div className="upload-icon">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            <p className="upload-text">
              Drop image here or <span className="upload-link">browse</span>
            </p>
            <p className="upload-sub">PNG, JPG, WEBP up to 5MB</p>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </div>
    </div>
  );
}

export default function AIImageComparisonTool() {
  const [image1, setImage1] = useState<ImageData | null>(null);
  const [image2, setImage2] = useState<ImageData | null>(null);
  const [focusArea, setFocusArea] = useState<string>("");
  const [mode, setMode] = useState<ComparisonMode>("compare");
  const [compareResult, setCompareResult] = useState<string | null>(null);
  const [localizeResult, setLocalizeResult] = useState<LocalizeResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<boolean>(false);

  const pollTask = useCallback(async (taskId: string): Promise<TaskResponse> => {
    while (!abortRef.current) {
      const res = await fetch(`${API_BASE}/tasks/${taskId}`, {
        headers: { "x-api-key": API_KEY }
      });

      if (!res.ok) {
        if (res.status === 404) throw new Error("Task not found or expired.");
        throw new Error(`Polling failed with status ${res.status}`);
      }

      const data: TaskResponse = await res.json();

      if (data.status === "completed") return data;
      if (data.status === "failed") throw new Error(data.error || "Task failed.");

      await new Promise((r) => setTimeout(r, POLL_INTERVAL));
    }
    throw new Error("Comparison was cancelled.");
  }, []);

  const handleCompare = async (): Promise<void> => {
    if (!image1 || !image2) return alert("Please upload both images.");
    setLoading(true);
    setCompareResult(null);
    setLocalizeResult(null);
    setError(null);
    abortRef.current = false;

    try {
      const endpoint = mode === "compare" ? "/compare" : "/localize";
      const formData = new FormData();
      formData.append("image1", image1.file);
      formData.append("image2", image2.file);
      formData.append("model", "claude");
      if (focusArea) formData.append("focus_area", focusArea);

      const submitRes = await fetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        headers: { "x-api-key": API_KEY },
        body: formData
      });

      if (!submitRes.ok) {
        if (submitRes.status === 403) throw new Error("Invalid API key.");
        if (submitRes.status === 400) throw new Error("Bad request. Check your images and try again.");
        throw new Error(`Submit failed with status ${submitRes.status}`);
      }

      const { task_id } = await submitRes.json();
      const taskResult = await pollTask(task_id);

      if (mode === "compare") {
        const data = taskResult.result as CompareResult;
        setCompareResult(data.result);
      } else {
        setLocalizeResult(taskResult.result as LocalizeResult);
      }
    } catch (err) {
      if (!abortRef.current) {
        setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = (): void => {
    abortRef.current = true;
    setImage1(null);
    setImage2(null);
    setFocusArea("");
    setMode("compare");
    setCompareResult(null);
    setLocalizeResult(null);
    setError(null);
    setLoading(false);
  };

  const formatResult = (text: string): React.ReactNode[] => {
    return text.split("\n").map((line, i) => {
      if (line.startsWith("**") && line.includes(":**")) {
        const [label, ...rest] = line.split(":**");
        return (
          <p key={i} style={{ marginBottom: "8px" }}>
            <strong style={{ color: "#1a1a2e" }}>
              {label.replace(/\*\*/g, "")}:
            </strong>
            {rest.join(":**")}
          </p>
        );
      }
      if (/^\d+\./.test(line)) {
        return (
          <p key={i} style={{ marginLeft: "16px", marginBottom: "4px" }}>
            {line}
          </p>
        );
      }
      return line ? (
        <p key={i} style={{ marginBottom: "8px" }}>
          {line}
        </p>
      ) : (
        <br key={i} />
      );
    });
  };

  return (
    <Layout>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=Instrument+Serif:ital@0;1&display=swap');

        .aic-root {
          font-family: 'DM Sans', sans-serif;
          background: #f7f6f3;
          min-height: 100vh;
          padding: 40px 24px 60px;
          color: #1a1a2e;
        }

        .aic-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .aic-header h1 {
          font-family: 'Instrument Serif', serif;
          font-size: 2.4rem;
          font-weight: 400;
          letter-spacing: -0.5px;
          margin: 0 0 6px;
          color: #1a1a2e;
        }

        .aic-header p {
          color: #666;
          font-size: 0.95rem;
          margin: 0 0 4px;
        }

        .aic-header .hint {
          font-size: 0.82rem;
          color: #999;
        }

        .aic-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          max-width: 1000px;
          margin: 0 auto 28px;
        }

        .upload-panel {
          background: #fff;
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
        }

        .panel-header {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          font-size: 1rem;
          margin-bottom: 14px;
          color: #1a1a2e;
        }

        .drop-zone {
          border: 2px dashed #d8d5cf;
          border-radius: 12px;
          min-height: 280px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: border-color 0.2s, background 0.2s;
          position: relative;
          overflow: hidden;
          background: #fafaf8;
        }

        .drop-zone:not(.has-image):hover {
          border-color: #4a7cfc;
          background: #f0f4ff;
        }

        .drop-zone.has-image {
          cursor: default;
          border-style: solid;
          border-color: #d8d5cf;
        }

        .preview-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          position: absolute;
          inset: 0;
          border-radius: 10px;
        }

        .remove-btn {
          position: absolute;
          top: 10px;
          right: 10px;
          background: #e53935;
          color: white;
          border: none;
          border-radius: 6px;
          padding: 5px 12px;
          font-size: 0.8rem;
          font-weight: 600;
          cursor: pointer;
          z-index: 2;
          font-family: 'DM Sans', sans-serif;
          transition: background 0.15s;
        }

        .remove-btn:hover { background: #c62828; }

        .upload-prompt {
          text-align: center;
          padding: 20px;
        }

        .upload-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: #eef1f8;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 14px;
          color: #4a7cfc;
        }

        .upload-text {
          font-size: 0.9rem;
          color: #555;
          margin: 0 0 6px;
        }

        .upload-link { color: #4a7cfc; font-weight: 600; }

        .upload-sub {
          font-size: 0.78rem;
          color: #aaa;
          margin: 0;
        }

        .aic-controls {
          max-width: 1000px;
          margin: 0 auto;
        }

        .focus-label {
          display: block;
          font-size: 0.88rem;
          font-weight: 600;
          color: #444;
          margin-bottom: 8px;
        }

        .focus-input {
          width: 100%;
          padding: 12px 16px;
          border: 1.5px solid #e0ddd7;
          border-radius: 10px;
          font-size: 0.9rem;
          font-family: 'DM Sans', sans-serif;
          background: #fff;
          color: #1a1a2e;
          outline: none;
          transition: border-color 0.2s;
          box-sizing: border-box;
          margin-bottom: 4px;
        }

        .focus-input:focus { border-color: #4a7cfc; }
        .focus-input::placeholder { color: #bbb; }

        .focus-hint {
          font-size: 0.78rem;
          color: #aaa;
          margin-bottom: 24px;
          display: block;
        }

        .btn-row {
          display: flex;
          justify-content: center;
          gap: 12px;
        }

        .btn-compare {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #4a7cfc;
          color: white;
          border: none;
          border-radius: 10px;
          padding: 12px 28px;
          font-size: 0.95rem;
          font-weight: 600;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: background 0.2s, transform 0.1s;
        }

        .btn-compare:hover:not(:disabled) { background: #3366e6; transform: translateY(-1px); }
        .btn-compare:disabled { opacity: 0.6; cursor: not-allowed; }

        .btn-reset {
          background: #e8e6e1;
          color: #444;
          border: none;
          border-radius: 10px;
          padding: 12px 28px;
          font-size: 0.95rem;
          font-weight: 600;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-reset:hover { background: #d8d5cf; }

        .result-box {
          max-width: 1000px;
          margin: 32px auto 0;
          background: #fff;
          border-radius: 16px;
          padding: 28px 32px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
          line-height: 1.7;
          font-size: 0.93rem;
          color: #333;
          animation: fadeUp 0.4s ease;
        }

        .result-box h3 {
          font-family: 'Instrument Serif', serif;
          font-size: 1.3rem;
          font-weight: 400;
          margin: 0 0 16px;
          color: #1a1a2e;
          border-bottom: 1.5px solid #f0ede8;
          padding-bottom: 12px;
        }

        .error-box {
          max-width: 1000px;
          margin: 24px auto 0;
          background: #fff0f0;
          border: 1.5px solid #ffcdd2;
          border-radius: 12px;
          padding: 16px 20px;
          color: #c62828;
          font-size: 0.9rem;
          text-align: center;
        }

        .spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }

        .mode-toggle {
          display: flex;
          gap: 8px;
          margin-bottom: 20px;
        }

        .mode-btn {
          flex: 1;
          padding: 10px 16px;
          border: 1.5px solid #e0ddd7;
          border-radius: 10px;
          background: #fff;
          color: #666;
          font-size: 0.9rem;
          font-weight: 600;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: all 0.2s;
        }

        .mode-btn:hover:not(:disabled) { border-color: #4a7cfc; color: #4a7cfc; }
        .mode-btn.active { background: #4a7cfc; color: white; border-color: #4a7cfc; }
        .mode-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        .annotated-image-wrap {
          margin-bottom: 20px;
          border-radius: 12px;
          overflow: hidden;
          border: 1.5px solid #e0ddd7;
        }

        .annotated-image {
          width: 100%;
          display: block;
        }

        .bbox-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .bbox-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 14px;
          background: #f7f6f3;
          border-radius: 8px;
          font-size: 0.88rem;
        }

        .bbox-label { font-weight: 600; color: #1a1a2e; }
        .bbox-coords { color: #999; font-size: 0.8rem; font-family: monospace; }

        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 640px) {
          .aic-grid { grid-template-columns: 1fr; }
          .aic-header h1 { font-size: 1.8rem; }
        }
      `}</style>

      <div className="aic-root">
        <div className="aic-header">
          <h1>AI Image Comparison Tool</h1>
          <p>Upload two images to spot the differences using AI analysis</p>
          <span className="hint">
            For best results, use clear, high-resolution images
          </span>
        </div>

        <div className="aic-grid">
          <ImageUploadPanel
            label="Image 1 (Reference)"
            image={image1}
            onUpload={setImage1}
            onRemove={() => setImage1(null)}
          />
          <ImageUploadPanel
            label="Image 2 (Comparison)"
            image={image2}
            onUpload={setImage2}
            onRemove={() => setImage2(null)}
          />
        </div>

        <div className="aic-controls">
          <div className="mode-toggle">
            <button
              className={`mode-btn ${mode === "compare" ? "active" : ""}`}
              onClick={() => setMode("compare")}
              disabled={loading}
            >
              Text Compare
            </button>
            <button
              className={`mode-btn ${mode === "localize" ? "active" : ""}`}
              onClick={() => setMode("localize")}
              disabled={loading}
            >
              Localize Differences
            </button>
          </div>

          <label className="focus-label">Focus Area (Optional)</label>
          <input
            className="focus-input"
            type="text"
            placeholder="e.g., 'top-left' or 'center'"
            value={focusArea}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFocusArea(e.target.value)
            }
          />
          <span className="focus-hint">
            Specify a region for the AI to focus on (e.g., top-left, center)
          </span>

          <div className="btn-row">
            <button
              className="btn-compare"
              onClick={handleCompare}
              disabled={loading || !image1 || !image2}
            >
              {loading ? (
                <>
                  <span className="spinner" /> Analyzing…
                </>
              ) : (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  {mode === "compare" ? "Compare Images" : "Localize Differences"}
                </>
              )}
            </button>
            <button className="btn-reset" onClick={handleReset}>
              Reset
            </button>
          </div>
        </div>

        {error && <div className="error-box">{error}</div>}

        {compareResult && (
          <div className="result-box">
            <h3>Analysis Results</h3>
            {formatResult(compareResult)}
          </div>
        )}

        {localizeResult && (
          <div className="result-box">
            <h3>Localized Differences</h3>
            {localizeResult.annotated_image && (
              <div className="annotated-image-wrap">
                <img
                  src={`data:image/png;base64,${localizeResult.annotated_image}`}
                  alt="Annotated differences"
                  className="annotated-image"
                />
              </div>
            )}
            {localizeResult.bboxes.length > 0 && (
              <div className="bbox-list">
                {localizeResult.bboxes.map((b, i) => (
                  <div key={i} className="bbox-item">
                    <span className="bbox-label">{i + 1}. {b.label}</span>
                    <span className="bbox-coords">
                      [{b.bbox.join(", ")}]
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
